import { ApiCustomer } from '@vend/types'
import { formatDistanceToNow } from 'date-fns'
import { HydratedSale, isLoadedProduct } from './hydrateSales'

interface ProductSummaryData {
  id: string
  name: string
  image?: string
  count: number
  productID?: string
  hasFailedToLoad?: boolean
}

export interface SaleSummary {
  id: string
  products: Array<{
    name: string
    productID: string
    image?: string
  }>
  customer: ApiCustomer | undefined
  parkedAt: string
  note: string
}

const generateName = (product: ProductSummaryData) => {
  if (product.hasFailedToLoad) {
    return { name: 'Product not found', productID: product.id }
  }

  return {
    name: `${product.count} × ${product.name}`,
    productID: product.id,
    image: product.image,
  }
}

const getParkedAtMessage = (sale: HydratedSale) =>
  `Parked ${formatDistanceToNow(new Date(sale.updated_at), {
    addSuffix: true,
  })} by ${sale.user?.display_name || sale.user?.username}`

const generateSummary = (sale: HydratedSale): SaleSummary => {
  const productSummaryMap: Record<string, ProductSummaryData> = {}

  sale.line_items.forEach(lineItem => {
    if (productSummaryMap[lineItem.product_id]) {
      productSummaryMap[lineItem.product_id].count += lineItem.quantity
    } else if (isLoadedProduct(lineItem.product)) {
      productSummaryMap[lineItem.product_id] = {
        name: lineItem.product.name,
        image: lineItem.product.image_url,
        id: lineItem.product_id,
        count: lineItem.quantity,
      }
    } else {
      productSummaryMap[lineItem.product_id] = {
        name: '',
        id: lineItem.product_id,
        count: lineItem.quantity,
        hasFailedToLoad: true,
      }
    }
  })

  const productSummaryList = Object.values(productSummaryMap).map(generateName)

  return {
    id: sale.id,
    products: productSummaryList,
    customer: sale.customer,
    parkedAt: getParkedAtMessage(sale),
    note: sale.note,
  }
}

export const generateSaleSummaries = (sales: HydratedSale[]) =>
  sales.map(generateSummary)
